import OfficeBuilding from 'mdi-material-ui/OfficeBuilding';
import SectionCard from '../components/SectionCard';
import useMsftReferral from '../hooks/useMsftReferral';
import DetailsGrid from '../../../components/DetailsGrid/DetailsGrid';
import { DealTypeMap } from './constants';
import NumberFormat from 'react-number-format';
import { formatIsoToDateOnlyString } from 'packages/cosell/src/utilities/formatDate';
import useMsftVendorSettings from '../hooks/useMsftVendorSettings';
import { useMemo } from 'react';

const DealDetailsSection = ({ tackleId }: { tackleId: string }) => {
  const { data: referral } = useMsftReferral({ tackleId });
  const { data: vendorSettings } = useMsftVendorSettings();

  /** We need to get the partnerLocation data for the matching mpnId to
   *  display the location name
   */
  const partnerLocation = useMemo(() => {
    if (!referral?.mpnId || !vendorSettings?.partnerLocations) return null;
    return (
      vendorSettings.partnerLocations.find((x) => x.mpnId === referral.mpnId) ??
      null
    );
  }, [referral?.mpnId, vendorSettings?.partnerLocations]);

  const requirementAttributes =
    referral.details?.requirements?.additionalRequirements?.attributes;
  /** Convert the requirement attributes into structured data for display
   *  TODO: consider moving this to the request zod parser if it is used in multiple components
   */
  const requirements = useMemo(() => {
    return requirementAttributes.reduce(
      (acc, item) => {
        acc[item.type] = item.id;
        return acc;
      },
      {
        SolutionArea: '',
        SolutionPlay: '',
        CustomerMarketplaceIntent: '',
      },
    );
  }, [requirementAttributes]);

  if (!referral) return null;

  const dataRows: { content: React.ReactNode; title: string }[][] = [
    [
      {
        title: 'Deal name',
        content: referral.name,
      },
      {
        title: 'Deal type',
        content: DealTypeMap[referral.metadata?.dealType],
      },
    ],
    [
      {
        title: 'Marketplace purchase intent',
        content: requirements.CustomerMarketplaceIntent,
      },
      {
        title: 'Location',
        content: partnerLocation?.name,
      },
    ],
    [
      {
        title: 'Estimated value',
        content: referral.details.dealValue ? (
          <NumberFormat
            value={referral.details.dealValue}
            displayType="text"
            thousandSeparator={true}
            decimalScale={2}
            fixedDecimalScale={true}
            suffix={` ${referral.details.currency}`}
          />
        ) : null,
      },
      {
        title: 'Estimated close date',
        content: referral.details?.closingDateTime
          ? formatIsoToDateOnlyString(referral.details.closingDateTime)
          : null,
      },
    ],
    [
      {
        title: 'CRM ID',
        content: referral.metadata?.crmId,
      },
      {
        title: 'Marketing campaign ID',
        content: referral.campaignId,
      },
    ],
    [
      {
        title: 'Tackle ID',
        content: referral.metadata?.tackleId,
      },
    ],
    [
      {
        title: 'Solution area',
        content: requirements.SolutionArea,
      },
      {
        title: 'Solution play',
        content: requirements.SolutionPlay,
      },
    ],
    [
      {
        title: 'Customer need',
        content: referral.details?.notes,
      },
    ],
  ];

  return (
    <SectionCard
      icon={
        <OfficeBuilding
          data-testid="office-building-icon"
          style={{ fill: '#253858' }}
        />
      }
      title="Deal Details"
      bodyStyle={{ paddingBlock: '0', paddingBottom: '1.5rem' }}
    >
      <DetailsGrid>
        {dataRows.map((row, index) => (
          <DetailsGrid.Row key={index}>
            {row.map(({ title, content }, i) => (
              <DetailsGrid.Detail key={`${title}${i}`} title={title}>
                {content ?? ' - '}
              </DetailsGrid.Detail>
            ))}
          </DetailsGrid.Row>
        ))}
      </DetailsGrid>
    </SectionCard>
  );
};

export default DealDetailsSection;
